import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { useTenant } from './TenantContext'
import { supabase } from '../lib/supabase'
import { logger } from '../lib/productionLogger'
import type { Database } from '../lib/supabase'

type Profile = Database['public']['Tables']['profiles']['Row']
type ProfileState = 'idle' | 'loading' | 'loaded' | 'missing' | 'error'

interface ProfileContextValue {
  profileState: ProfileState
  profile: Profile | null
  error: string | null
  retry: () => void
  refetch: () => Promise<void>
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined)

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { authState, user } = useAuth()
  const { bootstrapStatus, bootstrapVersion, tenantId } = useTenant()
  const [profileState, setProfileState] = useState<ProfileState>('idle')
  const [profile, setProfile] = useState<Profile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)
  const fetchAttemptRef = useRef(0)
  const bootstrapRefetchedRef = useRef(false) // Track if we've refetched after bootstrap

  const fetchProfile = useCallback(async (userId: string) => {
    const attemptId = ++fetchAttemptRef.current
    logger.log('[ProfileProvider] Fetching profile (READ ONLY)', { userId, attemptId })

    try {
      setProfileState('loading')
      setError(null)

      // ========================================================================
      // READ ONLY: Query existing profile, NEVER create it
      // TenantContext is responsible for calling ensure_profile_and_tenant
      // ========================================================================
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (fetchError) {
        throw fetchError
      }

      if (!mountedRef.current) return

      // ========================================================================
      // CASE 1: Profile exists with tenant_id - SUCCESS
      // ========================================================================
      if (data && data.tenant_id) {
        logger.log('[ProfileProvider] Profile loaded with tenant_id', {
          userId,
          attemptId,
          tenantId: data.tenant_id,
        })
        setProfile(data as Profile)
        setProfileState('loaded')
        return
      }

      // ========================================================================
      // CASE 2: Profile exists but tenant_id is NULL (invited user)
      // ========================================================================
      if (data && !data.tenant_id) {
        logger.log('[ProfileProvider] Profile exists with null tenant_id (invited user)', {
          userId,
          profileId: data.id,
        })
        setProfile(data as Profile)
        setProfileState('loaded')
        return
      }

      // ========================================================================
      // CASE 3: Profile doesn't exist yet
      // Let TenantContext create it via ensure_profile_and_tenant
      // ========================================================================
      logger.log('[ProfileProvider] Profile not found - waiting for TenantContext to create it', {
        userId,
        attemptId,
      })
      setProfile(null)
      setProfileState('missing')
      return

    } catch (e) {
      logger.error('[ProfileProvider] Fetch error:', e)
      if (!mountedRef.current) return

      const errorMessage = e instanceof Error ? e.message : 'Failed to load profile'
      setProfileState('error')
      setError(errorMessage)
    }
  }, [])

  // React to auth state changes
  useEffect(() => {
    mountedRef.current = true

    if (authState === 'loading') {
      // Auth still loading, stay idle
      setProfileState('idle')
      return
    }

    if (authState === 'signedOut') {
      // User signed out, clear profile
      logger.log('[ProfileProvider] User signed out, clearing profile')
      setProfile(null)
      setProfileState('idle')
      setError(null)
      return
    }

    if (authState === 'error') {
      // Auth failed, can't fetch profile
      logger.log('[ProfileProvider] Auth error, cannot fetch profile')
      setProfileState('error')
      setError('Authentication failed')
      return
    }

    if (authState === 'signedIn' && user) {
      // User signed in, fetch profile
      fetchProfile(user.id)
    }

    return () => {
      mountedRef.current = false
    }
  }, [authState, user, fetchProfile])

  // Watch bootstrap signals from TenantContext
  // When TenantContext completes bootstrap, refetch profile exactly once
  useEffect(() => {
    // Only refetch if profile is missing and bootstrap completed
    if (profileState !== 'missing' || !user) {
      return
    }

    // If bootstrap is running, show loading state
    if (bootstrapStatus === 'running') {
      logger.log('[ProfileProvider] Bootstrap running - waiting for completion')
      return
    }

    // If bootstrap completed (done) or version incremented, refetch once
    if ((bootstrapStatus === 'done' || bootstrapVersion > 0) && !bootstrapRefetchedRef.current) {
      logger.log('[ProfileProvider] Bootstrap complete - refetching profile once', {
        bootstrapStatus,
        bootstrapVersion,
      })
      bootstrapRefetchedRef.current = true
      fetchProfile(user.id)
      return
    }

    // If TenantContext already resolved via CASE 1 (existing profile+tenant, no
    // bootstrap RPC needed) but profileState is still 'missing', trigger a
    // one-shot refetch. This handles the race where ProfileContext first fetched
    // before TenantContext had set the tenant.
    if (bootstrapStatus === 'idle' && tenantId && !bootstrapRefetchedRef.current) {
      logger.log('[ProfileProvider] Tenant resolved without bootstrap - retrying profile fetch')
      bootstrapRefetchedRef.current = true
      fetchProfile(user.id)
      return
    }

    // If bootstrap failed, show error
    if (bootstrapStatus === 'error') {
      logger.warn('[ProfileProvider] Bootstrap failed - profile not created')
      setProfileState('error')
      setError('Failed to initialize your account. Please sign out and try again.')
      return
    }
  }, [profileState, user, bootstrapStatus, bootstrapVersion, tenantId, fetchProfile])

  // Reset bootstrap refetch flag when user changes
  useEffect(() => {
    bootstrapRefetchedRef.current = false
  }, [user?.id])

  const retry = useCallback(() => {
    if (!user) {
      logger.warn('[ProfileProvider] Cannot retry without user')
      return
    }
    logger.log('[ProfileProvider] Retrying profile fetch')
    fetchProfile(user.id)
  }, [user, fetchProfile])

  const refetch = useCallback(async () => {
    if (!user) {
      logger.warn('[ProfileProvider] Cannot refetch without user')
      return
    }
    await fetchProfile(user.id)
  }, [user, fetchProfile])

  return (
    <ProfileContext.Provider
      value={{
        profileState,
        profile,
        error,
        retry,
        refetch
      }}
    >
      {children}
    </ProfileContext.Provider>
  )
}

export function useProfile() {
  const context = useContext(ProfileContext)
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}
